// Radha — reusable table search for the module panels.
//
// Collections had a bespoke search+risk filter; this generalises just the text
// search so every other table gets it with two lines: build a searchable string
// per row, drop the <SearchBar/> in the panel's toolbar slot, and render the
// filtered rows. Pure client-side over the already-loaded rows — no new query.
import React, { useMemo, useState } from "react";

import { exportTable } from "../lib/exportTable";

export function useTableSearch<T>(rows: T[] | undefined, toText: (r: T) => string) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    const list = rows ?? [];
    return n === "" ? list : list.filter((r) => toText(r).toLowerCase().includes(n));
  }, [rows, q]);
  return { q, setQ, filtered };
}

export function SearchBar({
  q, setQ, count, total, placeholder = "Search…",
}: {
  q: string; setQ: (v: string) => void; count: number; total: number; placeholder?: string;
}) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "0 0 12px", flexWrap: "wrap" }}>
      <input
        placeholder={placeholder}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="sanctuary-btn"
        style={{ padding: "7px 11px", border: "1px solid var(--border)", borderRadius: 9, fontSize: 13, minWidth: 220, background: "var(--surface)", color: "var(--fg)" }}
      />
      <span style={{ color: "var(--fg-muted)", fontSize: 12 }}>{count} of {total}</span>
    </div>
  );
}

// Reusable "Export Excel" button: downloads the current (filtered) rows as a
// single-sheet workbook. Give it a sheet/file name and a thunk that maps the
// visible rows to flat records — so search narrows the export too.
export function ExportButton({ name, rows }: { name: string; rows: () => Record<string, unknown>[] }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await exportTable(name, rows()); } finally { setBusy(false); }
  };
  return (
    <button onClick={go} disabled={busy || rows().length === 0}
      title={`Download the ${name} table as Excel (${rows().length} rows).`}
      className="sanctuary-btn"
      style={{ marginLeft: "auto", padding: "6px 13px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 9, cursor: "pointer", background: "var(--surface)", color: "var(--fg-muted)" }}>
      {busy ? "Exporting…" : "Export Excel"}
    </button>
  );
}
